import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Card, CardContent, CardHeader, Stack } from '@mui/material'
import { RootState } from '../redux'
import EmptyState from './EmptyState'
import LoadingState from './LoadingState'
import { usePolicy, useSectors } from '../hooks'
import { useTranslation } from 'react-i18next'
import { Project, Sector } from '../types'
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import SectorDeleteButton from './SectorDeleteButton'
import SectorEditButton from './SectorEditButton'
import SectorCreateButton from './SectorCreateButton'
import { ProjectStateType } from '../enums/ProjectStateType'

interface SectorTableProps {
  project?: Project
}

export default function SectorTable({ project }: SectorTableProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The policies.
   */
  const { canEditProject } = usePolicy()

  /**
   * The sectors.
   */
  const sectors: Sector[] = useSectors()

  /**
   * Indicates if sectors are being loaded.
   */
  const isLoading: boolean = useSelector((state: RootState) => state.sectors.loading)

  /**
   * Indicates if there are no sectors.
   */
  const isEmpty = sectors.length === 0

  /**
   * The grid columns.
   */
  const columns: GridColDef<Sector>[] = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      width: 200,
      editable: false
    }
  ]

  if (project && project.state !== ProjectStateType.ARCHIVED && canEditProject(project)) {
    columns.push({
      field: t('actions'),
      type: 'actions',
      headerName: t('actions'),
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      resizable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          flexGrow={0}
          spacing={1}
          height="100%"
        >
          <SectorEditButton project={project} sector={row} />
          <SectorDeleteButton project={project} sector={row} />
        </Stack>
      )
    })
  }

  /**
   * The pagination model.
   */
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0
  })

  return (
    <Card>
      <CardHeader title={t('sectors')} action={<SectorCreateButton project={project} />} />
      <CardContent>
        {isLoading ? (
          <LoadingState />
        ) : isEmpty ? (
          <EmptyState title={t('noSectors')} />
        ) : (
          <DataGrid
            rows={sectors}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            disableRowSelectionOnClick
          />
        )}
      </CardContent>
    </Card>
  )
}
